"use client";

/**
 * The market now, as one line with a link in the home page's facts block
 * (UX plan 3.1, decision 8, 2026-10-29). It was a card of three figures
 * under the tools; before that the hero carried the numbers (owner,
 * 2026-10-26: statistics up top do not help a first look). The signed-in
 * hub keeps its charts, and /trends has the full picture.
 *
 * The figures come from the visitor's own payload (lib/use-personal, the
 * /me/home read the goal question makes anyway, so no request of its own):
 * the city when no area is known, their area otherwise.
 *
 *   sale   1 m² price and its 12-week change
 *   rent   the typical monthly rent and 1 m²
 *
 * Never a count of our listings. Nothing renders until the payload is
 * here; the line sits far under the first screen, so it moves nothing in
 * view when it arrives.
 */
import { Fragment, type ReactNode } from "react";
import Link from "@/lib/localized-link";
import { ChevronRight } from "lucide-react";
import { useT } from "@/lib/locale-context";
import { formatPrice } from "@/lib/api";
import { signedPct } from "@/lib/format/change";
import { trackEvent } from "@/lib/tracking";
import { usePersonal } from "@/lib/use-personal";

/** A template with each {value} as its own node, so the figures stand
 *  apart from the words around them. */
function fillNodes(template: string, vars: Record<string, ReactNode>): ReactNode[] {
  return template.split(/(\{\w+\})/).map((part, i) => {
    const m = /^\{(\w+)\}$/.exec(part);
    return <Fragment key={i}>{m && m[1] in vars ? vars[m[1]] : part}</Fragment>;
  });
}

const FIGURE = "font-semibold tabular-nums text-gray-900";

export default function VisitorMarketNow() {
  const t = useT();
  const { data } = usePersonal();
  const trend = data?.trend ?? null;
  if (!data || !trend || !(trend.now_ppm > 0)) return null;

  const place = trend.scope === "area" && data.trend_place ? data.trend_place : t("home.market.city");
  // The unit joined to its number by a no-break space (DESIGN.md).
  const money = (v: number) => <span className={FIGURE}>{`${formatPrice(Math.round(v))} AZN`}</span>;
  const ppm = money(trend.now_ppm);
  const line = trend.leased
    ? trend.now_price != null && trend.now_price > 0
      ? fillNodes(t("home.market.line.rent"), { place, rent: money(trend.now_price), ppm })
      : fillNodes(t("home.market.line.rent_ppm"), { place, ppm })
    : trend.change_pct != null
      ? fillNodes(t("home.market.line.sale"), { place, ppm, pct: <span className={FIGURE}>{signedPct(trend.change_pct, 1)}</span> })
      : fillNodes(t("home.market.line.sale_ppm"), { place, ppm });

  return (
    <p className="text-sm leading-relaxed text-gray-600">
      {line}{" "}
      {/* A grey link (the quiet UI): indigo is for the page's one primary action. */}
      <Link
        href="/trends"
        onClick={() => trackEvent("home.market.more")}
        className="inline-flex items-center gap-0.5 whitespace-nowrap font-semibold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
      >
        {t("flow.trends.title")}
        <ChevronRight className="h-4 w-4 text-gray-400" aria-hidden />
      </Link>
    </p>
  );
}
